import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, BookOpen, Clock, MessageCircle, GraduationCap, MapPin, Calendar, Award, FileText, ChevronRight } from 'lucide-react';
import { getUniversityBySlug } from './data/universityData';
import { useNoIndex } from '@/hooks/useSEO';
import CareerBridgeBlock from './components/CareerBridgeBlock';
import ProfileEvaluationCallout from './components/ProfileEvaluationCallout';

// ─── University Profile Template (SEO-rich) ───
const UniversityProfile = ({ uni, slug }) => {
  const counsellingHref = `/study-abroad/book-counselling/?source=study-abroad-university-pseo&country=${encodeURIComponent(uni.country || '')}&university=${encodeURIComponent(uni.name || '')}`;
  return (
  <div data-testid="sa-university-profile">
    {/* Hero */}
    <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)` }}>
      <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <GraduationCap className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">University Profile</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-white leading-tight mb-3">
            {uni.name}
          </h1>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className="inline-flex items-center gap-1.5 text-sm text-slate-300">
              <MapPin className="w-3.5 h-3.5 text-[#C5A028]" /> {uni.city}, {uni.country}
            </span>
            <span className="inline-flex items-center gap-1.5 text-sm text-slate-300">
              <Award className="w-3.5 h-3.5 text-[#C5A028]" /> {uni.ranking}
            </span>
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
              uni.type === 'Public' ? 'bg-green-500/20 text-green-300' : 'bg-blue-500/20 text-blue-300'
            }`}>
              {uni.type}
            </span>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to={counsellingHref}
              className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-7 py-3 rounded-full transition-all sa-pulse-cta"
              data-testid="sa-uni-cta-hero"
            >
              Apply with ZAFCO <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={`/study-abroad/${uni.countrySlug}/`}
              className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-sm px-7 py-3 rounded-full transition-all"
            >
              Study in {uni.country}
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* Key Info Grid */}
    <section className="bg-[#001F5C] py-8">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="sa-stat-card rounded-xl p-4 text-center">
          <div className="text-xs text-slate-400 mb-1">Avg. Tuition</div>
          <div className="text-lg font-bold font-syne text-[#C5A028]">
            {uni.averageTuition.currency} {uni.averageTuition.amount}
          </div>
          <div className="text-[10px] text-slate-500">per {uni.averageTuition.period}</div>
        </div>
        <div className="sa-stat-card rounded-xl p-4 text-center">
          <div className="text-xs text-slate-400 mb-1">IELTS Required</div>
          <div className="text-lg font-bold font-syne text-[#C5A028]">{uni.ieltsRequirement}</div>
          <div className="text-[10px] text-slate-500">overall band</div>
        </div>
        <div className="sa-stat-card rounded-xl p-4 text-center">
          <div className="text-xs text-slate-400 mb-1">Intakes</div>
          <div className="text-sm font-bold font-syne text-[#C5A028]">{uni.intakes.join(', ')}</div>
        </div>
        <div className="sa-stat-card rounded-xl p-4 text-center">
          <div className="text-xs text-slate-400 mb-1">Institution Type</div>
          <div className="text-lg font-bold font-syne text-[#C5A028]">{uni.type}</div>
        </div>
      </div>
    </section>

    {/* Content Sections */}
    <section className="py-14 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-10">
            {/* Popular Programs */}
            <div>
              <h2 className="text-xl font-bold font-syne text-[#003087] mb-4">Popular Programs</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {uni.popularPrograms.map((prog) => (
                  <div key={prog} className="flex items-center gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <GraduationCap className="w-4 h-4 text-[#003087] shrink-0" />
                    <span className="text-sm font-medium text-slate-800">{prog}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Highlights */}
            <div>
              <h2 className="text-xl font-bold font-syne text-[#003087] mb-4">Why {uni.name}?</h2>
              <div className="space-y-3">
                {uni.highlights.map((h) => (
                  <div key={h} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#C5A028]/20 flex items-center justify-center shrink-0 mt-0.5">
                      <ChevronRight className="w-3 h-3 text-[#C5A028]" />
                    </div>
                    <span className="text-sm text-slate-700">{h}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Admission Requirements */}
            <div>
              <h2 className="text-xl font-bold font-syne text-[#003087] mb-4">Admission Requirements</h2>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-slate-200">
                  <span className="text-sm text-slate-600">IELTS Score</span>
                  <span className="text-sm font-bold text-slate-900">{uni.ieltsRequirement}+ Overall</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-200">
                  <span className="text-sm text-slate-600">Application Intakes</span>
                  <span className="text-sm font-bold text-slate-900">{uni.intakes.join(', ')}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-slate-600">Tuition Range</span>
                  <span className="text-sm font-bold text-slate-900">{uni.averageTuition.currency} {uni.averageTuition.amount}/{uni.averageTuition.period}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Apply CTA */}
            <div className="bg-gradient-to-br from-[#003087] to-[#001F5C] rounded-2xl p-6 text-white">
              <h3 className="text-base font-bold font-syne mb-2">Apply to {uni.name.split(' ').slice(0, 3).join(' ')}</h3>
              <p className="text-sm text-slate-300 mb-4">
                Get expert guidance on application, visa, and scholarship opportunities.
              </p>
              <Link
                to={counsellingHref}
                className="block text-center bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm py-3 rounded-full transition-all"
                data-testid="sa-uni-apply-cta"
              >
                Book Free Counselling
              </Link>
            </div>

            {/* Quick Facts */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <h3 className="text-sm font-bold font-syne text-[#003087] mb-3">Quick Facts</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#C5A028]" />
                  <span className="text-slate-700">{uni.city}, {uni.country}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-[#C5A028]" />
                  <span className="text-slate-700">{uni.ranking}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#C5A028]" />
                  <span className="text-slate-700">{uni.intakes.length} intake(s) per year</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#C5A028]" />
                  <span className="text-slate-700">IELTS {uni.ieltsRequirement}+</span>
                </div>
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <h3 className="text-sm font-bold font-syne text-[#003087] mb-3">Explore More</h3>
              <div className="space-y-2">
                <Link to={`/study-abroad/${uni.countrySlug}/`} className="flex items-center justify-between text-sm text-slate-700 hover:text-[#003087] transition-colors">
                  Study in {uni.country} <ChevronRight className="w-4 h-4" />
                </Link>
                <Link to="/study-abroad/cost-calculator/" className="flex items-center justify-between text-sm text-slate-700 hover:text-[#003087] transition-colors">
                  Cost Calculator <ChevronRight className="w-4 h-4" />
                </Link>
                <Link to="/study-abroad/compare/" className="flex items-center justify-between text-sm text-slate-700 hover:text-[#003087] transition-colors">
                  Compare Destinations <ChevronRight className="w-4 h-4" />
                </Link>
                {uni.countrySlug === 'dubai' && (
                  <Link
                    to="/study-abroad/dubai/compare/"
                    className="flex items-center justify-between text-sm text-[#C5A028] hover:text-[#EAB308] font-semibold transition-colors"
                    data-testid="sa-uni-dubai-compare-link"
                  >
                    Compare Dubai Universities <ChevronRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Profile Evaluation Callout — converts uni-page visitors into evaluated leads */}
    <section className="bg-white py-10 sm:py-14">
      <div className="max-w-5xl mx-auto px-6">
        <ProfileEvaluationCallout
          context="university"
          countryHint={uni.countrySlug}
          sourceVariant="uni-prefooter"
        />
      </div>
    </section>

    {/* Career-Bridge Block — ZAFCO Global Network footer (Phase 5.9) */}
    <CareerBridgeBlock context="university" countryName={uni.country} />
  </div>
  );
};

// ─── Generic "Under Curation" Template ───
const UnderCurationTemplate = ({ title, contextLabel, contextDesc, pathParts }) => {
  // Thin placeholder content — keep out of the index but pass link equity.
  useNoIndex({ follow: true });
  return (
  <div data-testid="sa-dynamic-page">
    <section className="sa-hero-gradient">
      <div className="max-w-7xl mx-auto px-6 py-14 lg:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <BookOpen className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">{contextLabel}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-white leading-tight mb-4">
            {title || 'Study Abroad'}
          </h1>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Link to="/study-abroad/" className="hover:text-white transition-colors">Home</Link>
            {pathParts.filter(Boolean).map((part) => (
              <span key={part} className="flex items-center gap-2">
                <span>/</span>
                <span className="capitalize">{part.replace(/-/g, ' ')}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-6">
        <div className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-2xl p-8 lg:p-10 text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-[#003087]/10 flex items-center justify-center mx-auto mb-5">
            <Clock className="w-8 h-8 text-[#003087]" />
          </div>
          <h2 className="text-xl font-bold font-syne text-[#003087] mb-3">Page Under Curation</h2>
          <p className="text-sm text-slate-600 leading-relaxed max-w-lg mx-auto mb-6">{contextDesc}</p>
          <Link
            to="/study-abroad/book-counselling/?source=study-abroad-uni-under-curation"
            className="inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all sa-pulse-cta"
            data-testid="sa-dynamic-cta"
          >
            Book Free Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="bg-[#003087] rounded-2xl p-6 lg:p-8 text-white flex flex-col sm:flex-row items-center gap-6 mb-10">
          <div className="w-14 h-14 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <MessageCircle className="w-7 h-7 text-[#C5A028]" />
          </div>
          <div className="text-center sm:text-left">
            <h3 className="text-base font-bold font-syne mb-1">Need Immediate Assistance?</h3>
            <p className="text-sm text-slate-300">Our experts are available Mon-Sat, 10 AM - 7 PM IST.</p>
          </div>
          <a href="tel:+919990533327" className="shrink-0 inline-flex items-center gap-2 border border-white/30 hover:bg-white/10 text-white font-semibold text-sm px-6 py-2.5 rounded-full transition-all">
            Call Now
          </a>
        </div>

        <div>
          <h3 className="text-base font-bold font-syne text-slate-900 mb-4">Explore Popular Pages</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'Study in Germany', path: '/study-abroad/germany/' },
              { label: 'Study in UK', path: '/study-abroad/uk/' },
              { label: 'Cost Calculator', path: '/study-abroad/cost-calculator/' },
              { label: 'Compare Destinations', path: '/study-abroad/compare/' },
              { label: 'Book Free Counselling', path: '/study-abroad/book-counselling/' },
              { label: 'Scholarship Assessment', path: '/study-abroad/scholarship-assessment/' },
            ].map((link) => (
              <Link key={link.path} to={link.path} className="flex items-center justify-between bg-slate-50 hover:bg-slate-100 border border-slate-100 rounded-xl p-4 transition-colors group">
                <span className="text-sm font-medium text-slate-700 group-hover:text-[#003087]">{link.label}</span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#003087]" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  </div>
  );
};

// ─── Main Dynamic Page Component ───
const DynamicStudyAbroadPage = () => {
  const location = useLocation();
  const pathParts = location.pathname.replace('/study-abroad/', '').replace(/\/$/, '').split('/');

  // Check if this is a university route: /study-abroad/universities/{country}/{slug}
  const isUniversityRoute = pathParts[0] === 'universities' && pathParts.length >= 3;
  if (isUniversityRoute) {
    const uniSlug = pathParts[2];
    const uniData = getUniversityBySlug(uniSlug);
    if (uniData) {
      return <UniversityProfile uni={uniData} slug={uniSlug} />;
    }
  }

  // Parse title from path
  const title = pathParts
    .filter(Boolean)
    .map((part) => part.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()))
    .join(' \u2014 ');

  // Determine context
  const isUniversity = location.pathname.includes('/universities/');
  const isCareer = location.pathname.includes('/careers/');
  const isVisa = location.pathname.includes('/visa/');
  const isBlog = location.pathname.includes('/blog/');

  let contextLabel = 'Study Abroad';
  let contextDesc = 'This page is currently being curated by our expert team. Book a free consultation to get immediate, personalized details from our experts.';
  if (isUniversity) {
    contextLabel = 'University Profile';
    contextDesc = 'Detailed information about this institution is being prepared by our team. Book a free consultation to get immediate details about admissions, fees, and scholarships.';
  } else if (isCareer) {
    contextLabel = 'Career Guide';
    contextDesc = 'This career guide is being curated with the latest job market data. Speak with our counsellors for immediate career guidance.';
  } else if (isVisa) {
    contextLabel = 'Visa Guide';
    contextDesc = 'This comprehensive visa guide is being prepared by our immigration experts. Book a consultation for immediate visa assistance.';
  } else if (isBlog) {
    contextLabel = 'Resource Guide';
    contextDesc = 'This in-depth guide is being written by our education specialists. Explore our other resources or book a consultation.';
  }

  return <UnderCurationTemplate title={title} contextLabel={contextLabel} contextDesc={contextDesc} pathParts={pathParts} />;
};

export default DynamicStudyAbroadPage;
